export interface RawContextMarker {
  key: string;
  value: string;
}

const SELECTION_KEYS = new Set([
  'ids',
  'candidate_ids',
  'contact_ids',
  'application_ids',
  'app_ids',
]);

const LEGACY_NAMESPACED_CHOICE_RE =
  /\b(?:job|contact|candidate|application|interview|offer|requisition|department|office|project|user|template|flow|report|stage):\d+\s*\(/gi;

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Remove only exact record values that already exist in a hidden selection
// marker. Ordinary counts, years, compensation, and unrelated numbers remain
// untouched.
export const collapseMarkerBackedSelectionIDs = (
  visibleTemplate: string,
  markers: RawContextMarker[]
): string => {
  let visible = visibleTemplate;
  for (const marker of markers) {
    if (!SELECTION_KEYS.has(marker.key.toLowerCase())) continue;
    const values = marker.value
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    if (values.length === 0) continue;

    const list = `(?:\\[\\s*)?\\b${values
      .map((value) => `(?:id\\s*)?${escapeRegExp(value)}`)
      .join('\\s*,\\s*')}\\b(?:\\s*\\])?`;
    const withNoun = new RegExp(
      `\\b(?:the\\s+)?(?:selected\\s+)?` +
        `(candidates?|contacts?|applicants?|profiles?|applications?|records?|people)\\s+${list}`,
      'gi'
    );
    visible = visible.replace(withNoun, `the ${values.length} selected $1`);
    visible = visible.replace(
      new RegExp(list, 'g'),
      `${values.length} selected records`
    );
  }
  return visible;
};

// Older single-picker submissions rendered `job:21724 (Human label)` in the
// bubble. Preserve the label and remove only that namespaced machine prefix.
// The scanner balances nested parentheses so labels such as
// "Engineering (R&D)" are retained intact.
export const stripLegacyNamespacedChoiceValues = (input: string): string => {
  let output = '';
  let cursor = 0;
  LEGACY_NAMESPACED_CHOICE_RE.lastIndex = 0;
  for (let match = LEGACY_NAMESPACED_CHOICE_RE.exec(input); match; ) {
    const open = LEGACY_NAMESPACED_CHOICE_RE.lastIndex - 1;
    let depth = 1;
    let end = open + 1;
    while (end < input.length && depth > 0) {
      if (input[end] === '(') depth += 1;
      if (input[end] === ')') depth -= 1;
      end += 1;
    }
    if (depth !== 0) break;
    output += input.slice(cursor, match.index) + input.slice(open + 1, end - 1);
    cursor = end;
    LEGACY_NAMESPACED_CHOICE_RE.lastIndex = end;
    match = LEGACY_NAMESPACED_CHOICE_RE.exec(input);
  }
  return output + input.slice(cursor);
};
